// Up Next queue store. Computes via `build-up-next` edge function and lets
// users manually `playNext` / `addToQueue` from anywhere. Local manual items
// take precedence over recomputed server items.

import { useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";
import { playerStore, type EpisodeMeta } from "@/lib/playerStore";

export interface QueueItem {
  episode_id: string;
  slug: string | null;
  title: string;
  audio_url: string | null;
  duration_seconds: number | null;
  podcast_id: string;
  podcast_name: string | null;
  podcast_slug: string | null;
  podcast_image_url: string | null;
  source: "playlist" | "resume" | "saved" | "tracked" | "similar" | "manual";
  source_label: string;
  /** Items the user manually queued — pinned to the top across refreshes. */
  manual?: boolean;
}

interface UpNextState {
  items: QueueItem[];
  loading: boolean;
  currentPlaylistId: string | null;
}

type Listener = () => void;

const PERSIST_KEY = "atp:up-next-v1";

function loadManual(): QueueItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(PERSIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueueItem[];
    return Array.isArray(parsed) ? parsed.filter((x) => x?.manual) : [];
  } catch {
    return [];
  }
}

function persistManual(items: QueueItem[]) {
  if (typeof window === "undefined") return;
  try {
    const manual = items.filter((x) => x.manual);
    window.localStorage.setItem(PERSIST_KEY, JSON.stringify(manual));
  } catch { /* ignore */ }
}

let state: UpNextState = {
  items: loadManual(),
  loading: false,
  currentPlaylistId: null,
};

const listeners = new Set<Listener>();
function emit() { for (const l of listeners) l(); }
function setState(patch: Partial<UpNextState>) {
  state = { ...state, ...patch };
  emit();
  persistManual(state.items);
}

function dedupe(items: QueueItem[]): QueueItem[] {
  const seen = new Set<string>();
  const out: QueueItem[] = [];
  for (const it of items) {
    if (!it?.episode_id) continue;
    if (seen.has(it.episode_id)) continue;
    seen.add(it.episode_id);
    out.push(it);
  }
  return out;
}

let inflight: Promise<void> | null = null;

async function reload(limit = 5): Promise<void> {
  if (inflight) return inflight;
  setState({ loading: true });
  const current = playerStore.getState().current;
  inflight = (async () => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        setState({ loading: false });
        return;
      }
      const { data, error } = await supabase.functions.invoke("build-up-next", {
        body: {
          current_episode_id: current?.episodeId ?? null,
          current_playlist_id: state.currentPlaylistId,
          limit,
        },
      });
      if (error) throw error;
      const server: QueueItem[] = Array.isArray(data?.items) ? data.items : [];
      const manual = state.items.filter((x) => x.manual);
      const merged = dedupe([...manual, ...server]).slice(0, Math.max(limit, manual.length + 3));
      setState({ items: merged, loading: false });
    } catch {
      setState({ loading: false });
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export const upNextStore = {
  getState: () => state,
  subscribe(l: Listener) { listeners.add(l); return () => listeners.delete(l); },

  setCurrentPlaylist(playlistId: string | null) {
    if (state.currentPlaylistId === playlistId) return;
    setState({ currentPlaylistId: playlistId });
    void reload();
  },

  refresh() { return reload(); },

  refillIfLow(threshold = 2) {
    if (state.items.length <= threshold) return reload();
    return Promise.resolve();
  },

  /** Pop and play the head of the queue. Returns true if something played. */
  consumeHead(): boolean {
    const head = state.items[0];
    if (!head?.audio_url) return false;
    const meta: EpisodeMeta = {
      key: head.episode_id,
      episodeId: head.episode_id,
      src: head.audio_url,
      title: head.title,
      podcastName: head.podcast_name ?? undefined,
      artworkUrl: head.podcast_image_url ?? undefined,
      episodeSlug: head.slug,
    };
    playerStore.playEpisode(meta);
    setState({ items: state.items.slice(1) });
    void upNextStore.refillIfLow();
    return true;
  },

  /** Insert at the front of the queue as a manual pinned item. */
  playNext(item: Omit<QueueItem, "source" | "source_label" | "manual">) {
    const manualItem: QueueItem = {
      ...item,
      source: "manual",
      source_label: "PLAY NEXT",
      manual: true,
    };
    const next = dedupe([manualItem, ...state.items]);
    setState({ items: next });
  },

  /** Append to the queue as a manual pinned item. */
  addToQueue(item: Omit<QueueItem, "source" | "source_label" | "manual">) {
    const manualItem: QueueItem = {
      ...item,
      source: "manual",
      source_label: "QUEUED",
      manual: true,
    };
    setState({ items: dedupe([...state.items, manualItem]) });
  },

  remove(episodeId: string) {
    setState({ items: state.items.filter((x) => x.episode_id !== episodeId) });
    void upNextStore.refillIfLow();
  },

  reorder(fromIndex: number, toIndex: number) {
    const next = state.items.slice();
    const [moved] = next.splice(fromIndex, 1);
    if (!moved) return;
    next.splice(toIndex, 0, moved);
    setState({ items: next });
  },

  /** Mark an episode as completed so it stops appearing in Up Next. */
  async markCompleted(episodeId: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await supabase
        .from("episode_resume_positions")
        .upsert(
          {
            user_id: user.id,
            episode_id: episodeId,
            position_seconds: 0,
            completed_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,episode_id" },
        );
    } catch { /* ignore */ }
  },
};

export function useUpNext<T>(selector: (s: UpNextState) => T): T {
  return useSyncExternalStore(
    upNextStore.subscribe,
    () => selector(state),
    () => selector(state),
  );
}
